import { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import NavBar from "../common/NavBar";
import Footer from "../common/Footer";
import { useTranslation } from "react-i18next";

const BlogList = () => {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [articles, setArticles] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTag, setActiveTag] = useState(searchParams.get("tag") || "");
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 9;

  const getApiBaseUrl = () =>
    process.env.REACT_APP_API_URL || "http://localhost:8000";

  const getCurrentLanguage = useCallback(() => {
    const language = (i18n.language || "en").slice(0, 2).toLowerCase();
    return ["en", "de", "es"].includes(language) ? language : "en";
  }, [i18n.language]);

  const fetchArticles = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let url = `${getApiBaseUrl()}/api/blog/articles/?`;
      url += `language=${getCurrentLanguage()}&`;
      if (searchQuery) url += `search=${encodeURIComponent(searchQuery)}&`;
      if (activeTag) url += `tag=${encodeURIComponent(activeTag)}&`;
      const response = await fetch(url);
      if (!response.ok) throw new Error(t("error_fetching_posts"));
      const data = await response.json();
      setArticles(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, activeTag, t, getCurrentLanguage]);

  const fetchTags = useCallback(async () => {
    try {
      const response = await fetch(
        `${getApiBaseUrl()}/api/blog/tags/?language=${getCurrentLanguage()}`
      );
      if (!response.ok) return;
      const data = await response.json();
      setTags(data);
    } catch {
      // silently fail
    }
  }, [getCurrentLanguage]);

  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  useEffect(() => {
    setCurrentPage(1);
    fetchArticles();
  }, [fetchArticles]);

  useEffect(() => {
    const nextParams = new URLSearchParams(searchParams);
    if (activeTag) nextParams.set("tag", activeTag);
    else nextParams.delete("tag");
    setSearchParams(nextParams, { replace: true });
  }, [activeTag, searchParams, setSearchParams]);

  const totalPages = Math.ceil(articles.length / PAGE_SIZE);
  const paginatedArticles = articles.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <NavBar />

      {/* Hero banner */}
      <div className="bg-blue-500 text-white pt-20 md:pt-24 pb-10 md:pb-12 px-4 md:px-6 text-center">
        <h1 className="text-3xl md:text-4xl font-bold mb-2">{t("blog")}</h1>
        <p className="text-blue-100 text-base md:text-lg">{t("blog_subtitle")}</p>
      </div>

      {/* Search + tag filter bar */}
      <div className="bg-white shadow-sm py-4 px-4 md:px-6">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row gap-3 items-stretch md:items-center justify-between">
          {/* Search */}
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t("search_posts")}
            className="border border-gray-300 rounded-md px-4 py-2 text-sm w-full md:w-64 focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
          {/* Tag filters */}
          <div className="flex flex-wrap gap-2 items-center">
            <button
              onClick={() => setActiveTag("")}
              className={`px-3 py-1 rounded-full text-sm font-medium transition ${
                activeTag === ""
                  ? "bg-blue-500 text-white"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            >
              {t("all_tags")}
            </button>
            {tags.map((tag) => (
              <button
                key={tag.slug}
                onClick={() => setActiveTag(activeTag === tag.name ? "" : tag.name)}
                className={`px-3 py-1 rounded-full text-sm font-medium transition ${
                  activeTag === tag.name
                    ? "bg-blue-500 text-white"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              >
                {tag.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main content */}
      <main className="flex-1 max-w-5xl mx-auto w-full px-4 md:px-6 py-8 md:py-10">
        {loading && (
          <p className="text-center text-gray-500 py-20">{t("loading")}</p>
        )}
        {error && (
          <p className="text-center text-red-500 py-20">{error}</p>
        )}
        {!loading && !error && paginatedArticles.length === 0 && (
          <p className="text-center text-gray-500 py-20">
            {t("no_posts_found")}
          </p>
        )}
        {!loading && !error && paginatedArticles.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {paginatedArticles.map((article) => (
              <article
                key={article.slug}
                className="bg-white rounded-xl shadow hover:shadow-md transition flex flex-col overflow-hidden"
              >
                {/* Cover image */}
                {article.cover_image_url ? (
                  <img
                    src={article.cover_image_url}
                    alt={article.title}
                    className="w-full h-48 object-cover"
                  />
                ) : (
                  <div className="w-full h-48 bg-blue-100 flex items-center justify-center">
                    <span className="text-blue-400 text-4xl font-bold">
                      {article.title.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}

                <div className="p-5 flex flex-col flex-1">
                  {/* Tags */}
                  {article.tags && article.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-2">
                      {article.tags.map((tag) => (
                        <span
                          key={tag}
                          className="bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-full"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Title */}
                  <h2 className="text-gray-800 font-semibold text-lg leading-snug mb-2 line-clamp-2">
                    {article.title}
                  </h2>

                  {/* Excerpt */}
                  {article.meta_description && (
                    <p className="text-gray-500 text-sm mb-3 line-clamp-3 flex-1">
                      {article.meta_description}
                    </p>
                  )}

                  {/* Meta */}
                  <div className="text-xs text-gray-400 mb-4">
                    {article.author_name && (
                      <span>
                        {t("by_author")} {article.author_name} &middot;{" "}
                      </span>
                    )}
                    {formatDate(article.published_date)}
                  </div>

                  {/* Read more */}
                  <Link
                    to={`/blog/${article.slug}`}
                    className="mt-auto inline-block bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium text-center py-2 px-4 rounded-lg transition"
                  >
                    {t("read_more")}
                  </Link>
                </div>
              </article>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center gap-2 mt-10">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 rounded-md bg-gray-200 text-gray-700 disabled:opacity-40 hover:bg-gray-300 transition text-sm"
            >
              &laquo;
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button
                key={p}
                onClick={() => setCurrentPage(p)}
                className={`px-4 py-2 rounded-md text-sm font-medium transition ${
                  p === currentPage
                    ? "bg-blue-500 text-white"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              >
                {p}
              </button>
            ))}
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-4 py-2 rounded-md bg-gray-200 text-gray-700 disabled:opacity-40 hover:bg-gray-300 transition text-sm"
            >
              &raquo;
            </button>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default BlogList;
